/*
 * RingBuffer — a fixed-capacity time-series ring SHARED BY REFERENCE between the
 * model-variables section (the writer) and the chart elements (readers).
 *
 * The section owns one ring per variable row, push()es every live sample, and
 * seed()s the historian window into it. <vitesse-sparkline> and <vitesse-chart>
 * hold a reference to the SAME ring and read it in their own rAF redraw — so
 * toggling a row collapsed↔expanded swaps which element is mounted WITHOUT losing
 * a sample and without any per-tick re-seed (the old design's churn bug). The
 * ring is plain (non-reactive) state; the elements read it on the animation
 * clock, never via Lit reactivity, exactly the way the agent's chart reads its
 * dataRef in the RAF rather than through React render.
 *
 * `version` bumps on every mutation so the canvas sparkline's per-frame min/max
 * cache can skip the windowed scan between samples.
 *
 * Capacity is configurable per instance (constructor arg) — RING_CAPACITY below is
 * just the default sized for the live-monitor sparkline. Readers that don't push()
 * live samples (e.g. a one-shot historian query) can pass a larger capacity so
 * seed() doesn't silently drop rows the query already returned.
 */
export const RING_CAPACITY = 1024; // ~10s at 100Hz — same order as the symbol grid

export class RingBuffer {
  readonly capacity: number;
  readonly timestamps: Float64Array;
  readonly values: Float64Array;
  count = 0;
  head = 0;
  version = 0;

  /** `capacity` defaults to RING_CAPACITY (the live-monitor sparkline size); pass a
   *  larger value for readers that need to hold more than that, e.g. a one-shot
   *  historian query whose row count isn't bounded by a live per-frame budget. */
  constructor(capacity: number = RING_CAPACITY) {
    this.capacity = capacity;
    this.timestamps = new Float64Array(capacity);
    this.values = new Float64Array(capacity);
  }

  /** Append one sample (oldest is evicted once full). */
  push(tsMs: number, num: number): void {
    if (!Number.isFinite(num)) return;
    const i = (this.head + this.count) % this.capacity;
    this.timestamps[i] = tsMs;
    this.values[i] = num;
    if (this.count < this.capacity) this.count++;
    else this.head = (this.head + 1) % this.capacity;
    this.version++;
  }

  /** Replace contents with `samples` (any order; sorted ascending + capped). */
  seed(samples: { tsMs: number; num: number }[]): void {
    const sorted = samples
      .filter((s) => Number.isFinite(s.num))
      .sort((a, b) => a.tsMs - b.tsMs)
      .slice(-this.capacity);
    this.count = 0;
    this.head = 0;
    for (const s of sorted) {
      this.timestamps[this.count] = s.tsMs;
      this.values[this.count] = s.num;
      this.count++;
    }
    this.version++;
  }

  /**
   * Merge a historian seed UNDERNEATH whatever live samples already landed — the
   * seed window precedes live data, so prepend it and let the sort+cap settle
   * ordering. Used because the live stream can race ahead of the seed query.
   */
  mergeSeed(samples: { tsMs: number; num: number }[]): void {
    const existing: { tsMs: number; num: number }[] = [];
    for (let i = 0; i < this.count; i++) {
      const idx = (this.head + i) % this.capacity;
      existing.push({ tsMs: this.timestamps[idx], num: this.values[idx] });
    }
    this.seed([...samples, ...existing]);
  }

  /**
   * DENSIFY-and-merge a historian seed. The seed is publish-on-change (SPARSE — a
   * slowly-changing variable yields a handful of points seconds apart) while the
   * live tail is dense (~per frame). Stored verbatim (mergeSeed), each sparse seed
   * point falling off the left edge deletes a BIG chunk of the line (vs the dense
   * tail's smooth per-pixel scroll). So resample the seed onto a uniform `slotMs`
   * grid by carry-forward (LOCF) — matching the live density — before merging it
   * UNDER the live samples already in the ring. Mirrors the symbol grid's
   * seedFromHistory. `nowMs` bounds the carry-forward when no live sample has landed
   * yet; otherwise the live tail's oldest sample is the hand-off point.
   */
  seedDense(samples: { tsMs: number; num: number }[], slotMs: number, nowMs: number): void {
    const existing: { tsMs: number; num: number }[] = [];
    for (let i = 0; i < this.count; i++) {
      const idx = (this.head + i) % this.capacity;
      existing.push({ tsMs: this.timestamps[idx], num: this.values[idx] });
    }
    const sorted = samples.filter((s) => Number.isFinite(s.num)).sort((a, b) => a.tsMs - b.tsMs);
    const dense: { tsMs: number; num: number }[] = [];
    if (sorted.length > 0) {
      // Carry-forward up to where the live tail takes over (its oldest sample), or
      // to `now` when no live has landed — so the seed region is uniformly dense.
      const end = Math.max(sorted[sorted.length - 1].tsMs, existing.length > 0 ? existing[0].tsMs : nowMs);
      let si = 0;
      let last = sorted[0].num;
      for (let t = sorted[0].tsMs; t < end; t += slotMs) {
        while (si < sorted.length && sorted[si].tsMs <= t) {
          last = sorted[si].num;
          si++;
        }
        dense.push({ tsMs: t, num: last });
      }
    }
    this.seed([...dense, ...existing]);
  }

  /** Latest sample value, or null when empty. */
  last(): number | null {
    if (this.count === 0) return null;
    return this.values[(this.head + this.count - 1) % this.capacity];
  }

  /** Timestamp of the latest sample, or 0 when empty. */
  lastTs(): number {
    if (this.count === 0) return 0;
    return this.timestamps[(this.head + this.count - 1) % this.capacity];
  }
}
